import { createApi } from "@reduxjs/toolkit/query/react";
import { query } from "api";
import { ProjectTrace } from "types/ProjectTrace";
import { SalesNetworksResponse } from "types/SalesNetworks";
import { GetLinksResponse } from "types/Links";
import {
  transformCodeGammePac,
  transformAppoint,
} from "../utils/helpers/project";

export interface GetListOptions {
  filterData?: any;
  Sorts?:
  | {
    Member: string;
    SortDirection: number;
  }[]
  | null;
}

const transformSolutionRequest = (data: GetSolutionRequest) => ({
  ...data,
  code_gamme_pac: transformCodeGammePac(data.code_gamme_pac),
  filtres: {
    ...data.filtres,
    appoint: transformAppoint(data.filtres?.appoint),
  },
});
const rtkApi = createApi({
  keepUnusedDataFor: 0,
  reducerPath: "api",
  baseQuery: query(),
  tagTypes: ["Locations", "Villes"],
  endpoints: (builder) => ({
    getSalesNetworks: builder.query<SalesNetworksResponse, void>({
      query: () => ({ url: "Application/SalesNetworks", method: "GET" }),
    }),
    getTranslations: builder.query<any, { networkCode: string }>({
      query: ({ networkCode }) => ({
        url: "Application/Translations",
        method: "GET",
        params: { code: networkCode },
      }),
    }),
    //TODO: network code
    getFAQ: builder.query<
      FAQResponse,
      { networkCode: string; languageCode: string }
    >({
      query: ({ languageCode, networkCode }) => ({
        url: "Application/FAQ",
        method: "GET",
        params: { code: networkCode, culture: languageCode },
      }),
    }),
    getMaintenance: builder.query({
      query: () => ({
        url: `Application/GetMaintenance`,
        method: "GET",
      }),
    }),
    getLinks: builder.query<GetLinksResponse, { code: string }>({
      query: ({ code }) => ({
        url: `Application/GetLinks`,
        method: "GET",
        params: { code },
      }),
    }),
    getInformation: builder.query<
      InformationResponse,
      { networkCode?: string; }
    >({
      query: ({ networkCode }) => ({
        url: `Application/GetInformation`,
        method: "GET",
        params: { code: networkCode },
      }),
    }),
    getSystemUser: builder.query({
      query: () => ({
        url: `SystemUser/GetProfileData`,
        method: "GET",
      }),
    }),
    updateSystemUser: builder.mutation<SystemUser, { systemUser: SystemUser }>({
      query: ({ systemUser }) => {
        if (systemUser.images == null)
          systemUser = { ...systemUser, images: [] };

        return {
          url: `SystemUser/UpdateProfileData`,
          method: "POST",
          data: systemUser,
          headers: {
            "Content-Type": "application/json",
          },
        };
      },
    }),
    getLocations: builder.query<GetLocationsResponse, void>({
      query: () => ({
        url: `Moteur/GetLocations`,
        method: "GET",
      }),
      transformResponse: (res: GetLocationsResponse) => {
        return res.map((i) => ({ text: i.text, value: i.text }));
      },
      providesTags: () => [{ type: "Locations" }],
    }),
    getLocationInfo: builder.query<
      GetLocationInfoResponse,
      { departement: string; code: string; altitude?: number; bord_de_mer_montagne?: boolean }
    >({
      query: ({ code, departement, altitude, bord_de_mer_montagne }) => ({
        url: `Moteur/GetLocationInfo`,
        method: "GET",
        params: { code_ville: code, code_departement: departement, altitude, bdm: bord_de_mer_montagne },
      }),
    }),
    getDepartments: builder.query<GetDepartmentsResponse, void>({
      query: () => ({
        url: `Moteur/GetDepartments`,
        method: "GET",
      }),
      transformResponse: (res: GetDepartmentsResponse) => {
        return res.map((i) => ({ ...i, text: `${i.value}-${i.text}`, value: i.text, code: i.value }));
      },
      // providesTags: () => [{ type: "Departments" }],
    }),
    getVilles: builder.query<GetVillesResponse, { depCode: string }>({
      query: ({ depCode }) => ({
        url: `Moteur/GetVilles`,
        method: "GET",
        params: { code_departement: depCode },
      }),
      transformResponse: (res: GetVillesResponse) => {
        return res.map((i) => ({ ...i, text: i.value, value: i.value }));
      },
      providesTags: () => [{ type: "Villes" }],
    }),
    getSolutionElectric: builder.query<GetSolutionResponse, GetSolutionRequest>(
      {
        query: (data) => ({
          url: `Moteur/SolutionElectric`,
          method: "POST",
          data: transformSolutionRequest(data),
          isJson: true,
        }),
      }
    ),
    getSolutionHybride: builder.query<GetSolutionResponse, GetSolutionRequest>({
      query: (data) => ({
        url: `Moteur/SolutionHybride`,
        method: "POST",
        data: transformSolutionRequest(data),
        isJson: true,
      }),
    }),
    getEtude: builder.query<GetEtudeResponse, { id: number; code?: string }>({
      query: ({ id, code }) => ({
        url: `Etude/GetData`,
        method: "GET",
        params: { id, code },
        //isJson: true,
      }),
    }),
    getEtudesList: builder.query<
      EtudeResponse,
      { code?: string; pageSize?: number; page?: number; searchQuery?: string }
    >({
      query: ({ code, pageSize, page, searchQuery }) => ({
        url: `Etude/GetListData`,
        method: "GET",
        params: {
          code: code,
          pageSize,
          page,
          filterData: searchQuery,
        },
        //isJson: true,
      }),
    }),
    updateEtudeData: builder.mutation<
      GetEtudeResponse,
      { etudeDto: GetEtudeResponse; code?: string }
    >({
      query: ({ etudeDto, code }) => {
        // debugger;
        return {
          url: `Etude/UpdateFormData`, //UpdateFormDataJson
          method: "POST",
          data: etudeDto,
          params: { code },
          headers: {
            "Content-Type": "application/json",
          },
        };
      },
    }),
    updateEtudeDataJson: builder.mutation<
      GetEtudeResponse,
      {
        id: number;
        name?: string;
        code?: string;
        jsonData: string;
      }
    >({
      query: ({ id, name, code, jsonData }) => {
        return {
          url: `Etude/UpdateFormDataJson`,
          method: "POST",
          data: { id: id, jsonData: jsonData, code: code, name: name },
        };
      },
    }),
    deleteEtudeData: builder.mutation<
      void,
      { selected: number[]; code?: string }
    >({
      query: ({ selected, code }) => {
        return {
          url: `Etude/RemoveSelected`,
          method: "POST",
          data: { selected },
          params: { code },
          headers: {
            "Content-Type": "application/json",
          },
        };
      },
    }),
    updateEtudeName: builder.mutation<void, { etudeId: number; name: string }>({
      query: ({ etudeId, name }) => {
        return {
          url: `Etude/UpdateName`,
          method: "POST",
          data: { etudeId, name },
        };
      },
    }),
    updateEtudeNote: builder.mutation<
      void,
      { etudeVersionId: number; note: string }
    >({
      query: ({ etudeVersionId, note }) => {
        return {
          url: `Etude/UpdateNote`,
          method: "POST",
          data: { etudeVersionId, note },
        };
      },
    }),
    createEtudePDF: builder.mutation<
      any,
      {
        etudeIds: string;
        networkId?: number;
        showPrice?: boolean;
        isEtudeDetail?: boolean;
        culture?: string;
      }
    >({
      query: ({ etudeIds, networkId, showPrice, isEtudeDetail, culture }) => {
        return {
          url: `Etude/Etude`,
          method: "POST",
          data: {
            etudeIds,
            networkId,
            showPrice,
            isEtudeDetail,
            culture,
            _json: null,
          },
          responseHandler: (response: Response) => response.url,
        };
      },
    }),
    downloadEtudeCSV: builder.mutation<any, { etudeVersionId: number }>({
      query: ({ etudeVersionId }) => {
        return {
          url: `Etude/DownloadEtudeCSV?etudeVersionId=${etudeVersionId}`,
          method: "GET",
          responseHandler: async (response: any) => await response.blob(),
        };
      },
    }),
    duplicateEtude: builder.mutation<any, { etudeVersionId: number }>({
      query: ({ etudeVersionId }) => {
        return {
          url: `Etude/DuplicateEtude/${etudeVersionId}`,
          method: "POST",
          // responseHandler: async (response: any) => await response.blob(),
        };
      },
    }),
    getPdfURL: builder.mutation<
      any,
      {
        etudeIds: string;
        networkId?: number;
        showPrice?: boolean;
        isEtudeDetail?: boolean;
        culture?: string;
      }
    >({
      query: ({ etudeIds, networkId, showPrice, isEtudeDetail, culture }) => {
        return {
          url: `Etude/GetPdfURL`,
          method: "POST",
          data: {
            etudeIds,
            networkId,
            showPrice,
            isEtudeDetail,
            culture,
            _json: null,
          },
          responseHandler: (response: Response) => response.url,
        };
      },
    }),
    updateProjectTrace: builder.mutation<ProjectTrace, Partial<ProjectTrace>>({
      query: (projectTrace) => {
        return {
          url: `ProjectTrace/SaveProjectTrace`,
          method: "POST",
          data: projectTrace,
          headers: {
            "Content-Type": "application/json",
          },
          isJson: true,
        };
      },
    }),
    updateDHPData: builder.mutation<void, { data: DHPData }>({
      query: ({ data }) => ({
        url: `Etude/AddDHP`,
        method: "POST",
        data,
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
  }),
});

export const {
  useGetSalesNetworksQuery,
  useGetTranslationsQuery,
  useGetFAQQuery,
  useGetMaintenanceQuery,
  useGetSystemUserQuery,
  useUpdateSystemUserMutation,
  useGetLocationsQuery,
  useGetLocationInfoQuery,
  useLazyGetLocationInfoQuery,
  useGetVillesQuery,
  useGetDepartmentsQuery,
  useLazyGetSolutionElectricQuery,
  useLazyGetSolutionHybrideQuery,
  useGetEtudeQuery,
  useGetEtudesListQuery,
  useUpdateEtudeDataMutation,
  useDeleteEtudeDataMutation,
  useUpdateEtudeNameMutation,
  useUpdateEtudeNoteMutation,
  useCreateEtudePDFMutation,
  useGetPdfURLMutation,
  useUpdateEtudeDataJsonMutation,
  useUpdateProjectTraceMutation,
  useDownloadEtudeCSVMutation,
  useDuplicateEtudeMutation,
  useUpdateDHPDataMutation,
  useGetInformationQuery,
  useGetLinksQuery,
} = rtkApi;

export default rtkApi;
